import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Pencil, Check, Copy, RefreshCw } from 'lucide-react';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';
import { TraitStyleConfig, AttributeStyleConfig, IconStyleId, DeviceModalConfig } from '../../types';

// Import API for self-fetching energy data
import { ultronTuringApi } from '../../../../services/ultronTuringApi';

export interface PowerMeterDevice {
  deviceId: string;
  baseSN?: string;
  deviceName?: string;
  nickname?: string;
  location?: string;
  groupName?: string;
  isOnline?: boolean;
  online?: boolean;  // Alternative online status field
  status?: 'online' | 'offline';  // Status from device
  watt?: number;
  voltage?: number;
  current?: number;
  wattHour?: number;
  energy?: number;        // Accumulated energy (kWh)
  powerFactor?: number;
  traits?: string[];
  attributes?: Record<string, unknown>;
  system?: {
    endpointRelationship?: {
      classification?: string;
    };
  };
}

export interface PowerMeterModalProps {
  device: PowerMeterDevice;
  onClose: () => void;
  onSaveNickname?: (device: PowerMeterDevice, nickname: string | null) => Promise<void>;
  config?: Partial<DeviceModalConfig>;
  isMergedDevice?: boolean;
  onToggleMerge?: (baseSN: string) => void;
  // 24H Energy chart data
  energyChartData?: Array<{ time: string; value: number }>;
  isLoadingEnergyChart?: boolean;
}

/**
 * PowerMeter Modal Template
 * 
 * 電錶顯示 Modal (只讀，無控制)
 * - 即時功率 (W/kW)
 * - 電壓 (V)
 * - 電流 (A)
 * - 累積用電 (kWh)
 */
export const Template01_Dashboard: React.FC<PowerMeterModalProps> = ({
  device,
  onClose,
  onSaveNickname,
  config,
  isMergedDevice = false,
  onToggleMerge,
  energyChartData = [],
  isLoadingEnergyChart = false,
}) => {
  // ✅ Determine online status from data sources (no hardcoded logic)
  const isDeviceOnline = device.isOnline === true || 
                         device.online === true || 
                         device.status === 'online';
  
  // Nickname editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(device.nickname || device.deviceName || '');
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // ✅ 自行獲取能源圖表數據 - 在 modal 打開時觸發
  // 這樣可以確保每次打開 modal 都是獲取該設備的數據，不會有 race condition
  const [selfFetchedChartData, setSelfFetchedChartData] = useState<Array<{ time: string; value: number }>>([]);
  const [isSelfLoading, setIsSelfLoading] = useState(false);
  const mountedRef = useRef(true);
  
  useEffect(() => {
    mountedRef.current = true;
    
    const fetchEnergyData = async () => {
      const deviceSN = device.deviceId;
      console.log(`📊 [PowerMeter Modal] Fetching energy data for: ${deviceSN}`);
      
      setIsSelfLoading(true);
      setSelfFetchedChartData([]); // Clear old data immediately
      
      try {
        const report = await ultronTuringApi.generateReport([deviceSN], {
          duration: '24h',
          granularity: 'hour',
          format: 'summary',
          utcOffset: '+8:00'
        });
        
        // Check if component is still mounted
        if (!mountedRef.current) {
          console.log(`📊 [PowerMeter Modal] Component unmounted, ignoring response for: ${deviceSN}`);
          return;
        }
        
        if (report.success && report.data) {
          const reportData = report.data as Record<string, any>;
          const deviceData = reportData[deviceSN];
          
          if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
            // Helper to format time label
            const formatTimeLabel = (timeStr: string) => {
              const d = new Date(timeStr);
              const month = (d.getMonth() + 1).toString().padStart(2, '0');
              const day = d.getDate().toString().padStart(2, '0');
              const hour = d.getHours().toString().padStart(2, '0');
              return `${month}/${day} ${hour}:00`;
            };
            
            const chartData = deviceData.chartData
              .map((point: any) => ({
                time: point.time ? formatTimeLabel(point.time) : '',
                value: point.energy ?? 0
              }))
              .sort((a: any, b: any) => new Date(a.time).getTime() - new Date(b.time).getTime());
            
            console.log(`📊 [PowerMeter Modal] ✅ Got ${chartData.length} data points for: ${deviceSN}`);
            setSelfFetchedChartData(chartData);
          } else {
            console.log(`📊 [PowerMeter Modal] ❌ No chartData found for: ${deviceSN}`);
          }
        }
      } catch (error) {
        console.error(`📊 [PowerMeter Modal] Error fetching energy data:`, error);
      } finally {
        if (mountedRef.current) {
          setIsSelfLoading(false);
        }
      }
    };
    
    fetchEnergyData();
    
    // Cleanup: mark as unmounted to ignore stale responses
    return () => {
      mountedRef.current = false;
    };
  }, [device.deviceId]); // Only re-fetch if deviceId changes
  
  // Use self-fetched data if available, otherwise fallback to prop
  const chartDataToUse = selfFetchedChartData.length > 0 ? selfFetchedChartData : energyChartData;
  const isChartLoading = isSelfLoading || isLoadingEnergyChart;

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const hiddenComponents: string[] = config?.hiddenComponents || [];
  // Unified order: Attributes (BigIcon is always fixed at top)
  const traitOrder: string[] = config?.traitOrder || ['SensorDisplay', 'SensorChart'];

  // Sync editedName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && !isSavingRef.current && Date.now() > blockSyncUntilRef.current) {
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  // Device state
  const power = device.watt ?? 0;
  const voltage = device.voltage ?? 0;
  // API may return current in mA, convert to A if value > 100
  const rawCurrent = device.current ?? 0;
  const current = rawCurrent > 100 ? rawCurrent / 1000 : rawCurrent;
  const energy = device.energy ?? device.wattHour ?? 0;
  const powerFactor = device.powerFactor;
  
  const displayName = device.nickname || device.deviceName || device.deviceId;
  const displaySN = isMergedDevice ? device.baseSN : device.deviceId;

  // Multi-channel check
  const isMultiChannel = device.system?.endpointRelationship?.classification === 'multi_channel';

  // Format power display
  const formatPower = (watts: number): { value: string; unit: string } => {
    if (watts >= 1000) {
      return { value: (watts / 1000).toFixed(2), unit: 'kW' };
    }
    return { value: watts.toFixed(1), unit: 'W' };
  };

  const powerDisplay = formatPower(power);

  // Handle nickname save
  const handleSaveNickname = useCallback(async () => {
    if (onSaveNickname && editedName !== (device.nickname || device.deviceName)) {
      isSavingRef.current = true;
      blockSyncUntilRef.current = Date.now() + 3000;
      await onSaveNickname(device, editedName || null);
      isSavingRef.current = false;
    }
    setIsEditingName(false);
  }, [device, editedName, onSaveNickname]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveNickname();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [handleSaveNickname, device.nickname, device.deviceName]);

  // Copy SN to clipboard
  const handleCopySN = useCallback(() => {
    navigator.clipboard.writeText(displaySN || '');
  }, [displaySN]);

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="power_meter" 
              isOn={power > 0}
              size={20}
              styleId={smallIconStyle}
            />
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className="bg-slate-800 text-white px-2 py-1 rounded text-sm flex-1 min-w-0"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="text-white font-medium text-sm truncate">{displayName}</span>
            )}
            {isEditingName ? (
              <button onClick={handleSaveNickname} className="text-blue-400 hover:text-blue-300">
                <Check className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => setIsEditingName(true)} className="text-slate-400 hover:text-white">
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: isDeviceOnline,
                label: isDeviceOnline ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Big Icon - Main power display (circular) */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center py-4">
              <div className="w-32 h-32 rounded-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-2 border-blue-400/50 shadow-lg shadow-blue-500/30">
                <DeviceIcon
                  deviceType="power_meter"
                  isOn={true}
                  size={28}
                  styleId={bigIconStyle}
                  className="text-blue-400 mb-1"
                />
                <span className="text-3xl font-bold text-white">{powerDisplay.value}</span>
                <span className="text-base text-blue-300">{powerDisplay.unit}</span>
              </div>
            </div>
          )}

          {/* Dynamically rendered components based on traitOrder */}
          {traitOrder.map((componentName) => {
            if (hiddenComponents.includes(componentName)) return null;
            
            switch (componentName) {
              case 'SensorDisplay':
                return (
                  <div key="SensorDisplay" className="space-y-3">
                    {/* Voltage and Current */}
                    <div className="grid grid-cols-2 gap-3">
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{
                          value: voltage,
                          type: 'voltage',
                          label: '電壓',
                          unit: 'V',
                          precision: 1
                        }}
                      />
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{
                          value: current,
                          type: 'current',
                          label: '電流',
                          unit: 'A',
                          precision: 2
                        }}
                      />
                    </div>
                    {/* Accumulated energy */}
                    <DynamicAttribute
                      attributeName="SensorDisplay"
                      styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                      componentProps={{
                        value: energy / 1000,
                        type: 'energy',
                        label: '累積用電',
                        unit: 'kWh',
                        precision: 2
                      }}
                    />
                  </div>
                );
              
              case 'SensorChart':
                return (
                  <DynamicAttribute
                    key="SensorChart"
                    attributeName="SensorChart"
                    styleId={getAttributeStyleId('SensorChart', attributeStyles)}
                    componentProps={{
                      data: chartDataToUse.map(d => ({
                        time: d.time,
                        value: d.value
                      })),
                      title: '過去 24 小時用電量',
                      color: '#3b82f6',
                      unit: ' kWh',
                      height: 120,
                      loading: isChartLoading
                    }}
                  />
                );
              
              default:
                return null;
            }
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-800/50 space-y-3">
          {/* Multi-channel toggle */}
          {isMultiChannel && onToggleMerge && device.baseSN && (
            <div className="flex items-center justify-center gap-3 py-2">
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  isMergedDevice 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                單一方塊
              </button>
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  !isMergedDevice 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                個別方塊
              </button>
            </div>
          )}

          {/* Location and SN */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{displaySN}</span>
              <button onClick={handleCopySN} className="text-slate-400 hover:text-white">
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Dashboard;
